import "./App.css";
import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getAuthToken } from "./utils/auth";

import About from "./components/InnerPages/About";
import Causes from "./components/InnerPages/Causes";
import Contact from "./components/InnerPages/Contact";

import Login from "./components/LoginComponents/Login";
import LandingPage from "./components/LandingPage";
import Register from "./components/LoginComponents/Register";

import ThankYouPage from "./components/OtherComponents/ThankYou";
import UserDetails from "./components/DashboardComponents/UserDetails";

import Dashboard from "./components/DashboardComponents/Dashboard";
import AllApprovedPosts from "./components/Posts/AllApprovedPosts";
import AddPosts from "./components/Posts/AddPost";
import ViewSinglePost from "./components/Posts/ViewSinglePost";
import EditPosts from "./components/Posts/EditPost";
import AddPostMessagePage from "./components/OtherComponents/AddPostMessage";
// import Blog from "./components/InnerPages/Blog";
// import AddBlogs from "./components/Blogs/AddBlog";
// import ViewBlog from "./components/Blogs/ViewBlog";
// import EditBlogs from "./components/Blogs/EditBlog";
import AllUsers from "./components/User/AllUsers";
import ViewUserProfile from "./components/User/ViewUserProfile";
import UserAllPosts from "./components/User/UserAllPosts";
import UserDonations from "./components/User/UserDonations";
import EditUser from "./components/User/EditUser";
import Payment from "./components/Payment/Payment";
import SuccessPage from "./components/OtherComponents/Success";
import AdminPanel from "./components/AdminComponents/AdminPanel";
import AdminPosts from "./components/AdminComponents/AdminPosts";

import Donations from "./components/Payment/Donations";
import ForgotPassword from "./components/LoginComponents/ForgotPassword";
import VerifyResetPassword from "./components/OtherComponents/VerifyResetPassword";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY);

function App() {
  let navigate = useNavigate();
  const isAuthenticated = !!getAuthToken();
  const location = useLocation();
  const showPage = location?.state?.show;
  const page = showPage && location?.state?.page;
  const showPaymentSucess = location?.state?.paymentSuccess;
  const postStatusPage = location?.state?.postStatus;
  const isEditing = location?.state?.isEditing;
  const userData = location?.state?.userData;
  const role = location?.state?.role || localStorage.getItem("role");
  const [previousLocation, setPreviousLocation] = useState(location);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (
      location.pathname === "/payment" &&
      previousLocation.pathname === "/thankYou"
    ) {
      navigate("/admin");
    }
    setPreviousLocation(location);
  }, [location, navigate, previousLocation]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<About />} />
      <Route path="/causes" element={<Causes />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <Login />}
      />
      <Route path="/register" element={<Register />} />
      <Route path="/forgotPassword" element={<ForgotPassword />} />
      <Route path="/verifyEmail" element={<VerifyResetPassword />} />

      <Route
        path="/thanks"
        element={showPage ? <ThankYouPage page={page} /> : <Navigate to="/" />}
      />
      <Route
        path="/profile"
        element={isAuthenticated ? <UserDetails /> : <Navigate to="/login" />}
      />

      <Route
        path="/dashboard"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <Navigate to="/" />
            ) : (
              <Dashboard />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/dashboard/my-donations"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <Navigate to="/" />
            ) : (
              <Donations />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />

      <Route path="/posts" element={<AllApprovedPosts />} />
      <Route
        path="/add/post"
        element={isAuthenticated ? <AddPosts /> : <Navigate to="/" />}
      />
      <Route path="/view/post/:id" element={<ViewSinglePost />} />
      <Route
        path="/edit/post/:id"
        element={
          isAuthenticated ? (
            isEditing ? (
              <EditPosts />
            ) : role === "admin" ? (
              <Navigate to="/admin" />
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/post/status"
        element={
          postStatusPage ? (
            role === "admin" ? (
              <Navigate to="/admin" />
            ) : (
              <AddPostMessagePage />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />

      {/* <Route path="/blogs" element={<Blog />} />
      <Route
        path="/add/blog"
        element={isAuthenticated ? <AddBlogs /> : <Navigate to="/" />}
      />
      <Route path="/view/blog/:id" element={<ViewBlog />} />
      <Route
        path="/edit/blog/:id"
        element={isAuthenticated ? <EditBlogs /> : <Navigate to="/" />}
      /> */}

      <Route
        path="/users"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <AllUsers />
            ) : (
              <Navigate to="/" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route path="/view/user/profile/:id" element={<ViewUserProfile />} />
      <Route path="/view/user/posts/:id" element={<UserAllPosts />} />
      <Route path="/view/user/donations/:id" element={<UserDonations />} />
      <Route
        path="/edit/user/:id"
        element={isAuthenticated ? <EditUser /> : <Navigate to="/" />}
      />
      <Route
        path="/payment"
        element={
          isAuthenticated ? (
            userData ? (
              <Elements stripe={stripePromise}>
                <Payment />
              </Elements>
            ) : (
              <Navigate to="/login" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
      <Route
        path="/thankYou"
        element={showPaymentSucess ? <SuccessPage message={"Payment Success"}/> : <Navigate to="/" />}
      />

      <Route
        path="/admin"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <AdminPanel />
            ) : (
              <Navigate to="/" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/admin/my-posts"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <AdminPosts />
            ) : (
              <Navigate to="/" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/admin/my-donations"
        element={
          isAuthenticated ? (
            role === "admin" ? (
              <Donations />
            ) : (
              <Navigate to="/" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
    </Routes>
  );
}

export default App;
